/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/11/22, 9:23 PM
 *
 */
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { wrap } from 'popmotion';
import { useAppDispatch, useAppSelector } from 'stores';

import { setVerified } from 'features/auth';

import WelcomeImage from 'assets/img/welcome.svg';

import Button from 'components/Button';
import { Modal } from 'components/Modal/useModal';

export const orgAdminWelcomeSlides = [
  {
    link: WelcomeImage,
    pos: 1,
    title: 'Hello',
    subtitle:
      'Please press continue to see more of your Dashboard. You will get to know how you can use your dashboard.',
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-1.svg',
    pos: 2,
    title: 'Organization Members',
    subtitle:
      'Press on members tab on left side to view different kinds of members associated with your organization',
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-2.svg',
    pos: 3,
    title: 'Organization Roles',
    subtitle:
      'Click on any role to view members associated with that role. You currently can view operators and patients',
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-3.svg',
    pos: 4,
    title: 'Organization Patients',
    subtitle:
      'After clicking on patients, you get a list of patients. Click on the primary button to add patients. Click on patient to view more details of that patient',
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-4.svg',
    pos: 5,
    title: 'Add Patient',
    subtitle:
      'Click on Add Patient on Right Side of The Screen to add the basic details of a patient. Fill up the form and submit it.',
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-5.svg',
    pos: 6,
    title: 'Patient Details',
    subtitle:
      "On the right side, you can control everything about patients such as updating details, adding medical history and others. Below you can check for patient's test history and other details",
  },
  {
    link: 'https://sunya-bucket.s3.us-west-2.amazonaws.com/assets/howtouse/welcome-6.svg',
    pos: 7,
    title: 'More Patient Details',
    subtitle: 'You can scroll down more to see more details related to patient and see more tests ',
  },
];

type WelcomeModalImage = {
  link: string;
  pos: number;
  title: string;
  subtitle: string;
}[];

const variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    };
  },
};

interface IWelcomeModalProps {
  children: React.ReactNode;
  images: WelcomeModalImage;
}

export const WelcomeModal: React.FC<IWelcomeModalProps> = ({ children, images }) => {
  const dispatch = useAppDispatch();
  const setGuided = dispatch(setVerified());
  const guided = useAppSelector((state) => state.auth.verified);
  const [[page, direction], setPage] = useState([0, 0]);

  const imageIndex = wrap(0, images.length, page);

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  return (
    <Modal open={children ? false : !guided}>
      <Modal.Button type="open">{children ?? null}</Modal.Button>
      <Modal.Content width="max-w-7xl" opacity="opacity-60">
        <div className="flex flex-col justify-between space-y-4">
          <div className="flex flex-col">
            <Modal.Title>{images[imageIndex].title}</Modal.Title>
            <p className="text-lg font-medium text-gray-500">{images[imageIndex].subtitle}</p>
          </div>

          <div className="example-container">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={page}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                className="slider-img h-full w-full"
              >
                <img
                  src={images[imageIndex].link + '?ap=em'}
                  className="rounded-xl object-contain"
                  alt="Welcome Image"
                />
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="z-40 flex w-full justify-between self-end">
            <Modal.Button type="close">
              <Button onClick={() => dispatch(setVerified())}>Skip</Button>
            </Modal.Button>
            <div className="flex space-x-2">
              {images[imageIndex].pos !== 1 && (
                <Button onClick={() => paginate(-1)}>Previous</Button>
              )}

              {images[imageIndex].pos === images.length ? (
                <Modal.Button type="close">
                  <Button onClick={() => dispatch(setVerified())}>Finish</Button>
                </Modal.Button>
              ) : (
                <Button onClick={() => paginate(1)}>Next</Button>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};
